import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { api, apiError } from "@/lib/api";
import { Star, Loader2, MessageSquareQuote } from "lucide-react";
import { toast } from "sonner";

const Stars = ({ value, onChange, size = 16, testid }) => (
  <div className="flex items-center gap-0.5" data-testid={testid}>
    {[1, 2, 3, 4, 5].map((n) => (
      <button key={n} type="button" disabled={!onChange} onClick={() => onChange?.(n)} data-testid={onChange ? `star-${n}` : undefined}
        className={onChange ? "hover:scale-110 transition-transform" : "cursor-default"}>
        <Star size={size} className={n <= value ? "fill-gold text-gold" : "text-slate-300"} />
      </button>
    ))}
  </div>
);

export const ReviewsSection = ({ type, itemId }) => {
  const { t, user, lang } = useApp();
  const r = t.reviews;
  const [data, setData] = useState(null);
  const [eligible, setEligible] = useState([]);
  const [form, setForm] = useState({ booking_ref: "", rating: 5, comment: "" });
  const [saving, setSaving] = useState(false);

  const load = () => api.get(`/reviews/${type}/${itemId}`).then((res) => setData(res.data)).catch(() => setData({ count: 0, reviews: [] }));
  useEffect(() => { load(); }, [type, itemId]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!user) { setEligible([]); return; }
    api.get("/reviews/eligible").then((res) => {
      const list = res.data.filter((b) => b.type === type && b.item_id === itemId);
      setEligible(list);
      if (list[0]) setForm((f) => ({ ...f, booking_ref: list[0].ref }));
    }).catch(() => setEligible([]));
  }, [user, type, itemId]);

  const submit = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      await api.post("/reviews", form);
      toast.success(r.thanks); setEligible((l) => l.filter((b) => b.ref !== form.booking_ref)); setForm({ booking_ref: "", rating: 5, comment: "" }); load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail) || err.message); }
    finally { setSaving(false); }
  };

  return (
    <section id="reviews" className="mt-12" data-testid="reviews-section">
      <div className="flex items-center justify-between mb-5">
        <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2"><MessageSquareQuote size={20} className="text-gold" /> {r.title}</h2>
        {data?.average && (
          <div className="flex items-center gap-2 text-sm" data-testid="reviews-average">
            <Stars value={Math.round(data.average)} />
            <span className="font-bold text-tigris">{data.average}</span>
            <span className="text-slate-400">({data.count})</span>
          </div>
        )}
      </div>

      {eligible.length > 0 && (
        <form onSubmit={submit} data-testid="review-form" className="bg-white rounded-2xl border border-gold/30 shadow-sm p-5 mb-6 space-y-3">
          <p className="text-sm font-bold text-slate-700">{r.writeTitle}</p>
          {eligible.length > 1 && (
            <select data-testid="review-booking" className="text-sm border border-slate-200 rounded-lg px-3 py-2 bg-white" value={form.booking_ref} onChange={(e) => setForm({ ...form, booking_ref: e.target.value })}>
              {eligible.map((b) => <option key={b.ref} value={b.ref}>{b.ref} · {b.title}</option>)}
            </select>
          )}
          <Stars value={form.rating} onChange={(n) => setForm({ ...form, rating: n })} size={26} testid="review-stars" />
          <textarea data-testid="review-comment" rows={3} required minLength={3} maxLength={1000} placeholder={r.placeholder}
            className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-slate-50/60 focus:border-tigris outline-none text-sm"
            value={form.comment} onChange={(e) => setForm({ ...form, comment: e.target.value })} />
          <button type="submit" disabled={saving} data-testid="review-submit" className="btn-gold px-6 py-2.5 rounded-xl font-bold text-sm flex items-center gap-2">
            {saving ? <Loader2 size={16} className="animate-spin" /> : r.submit}
          </button>
        </form>
      )}
      {!user && <p className="text-xs text-slate-400 mb-5">{r.loginHint} <Link to="/auth" className="text-tigris font-semibold underline">{t.auth.login}</Link></p>}

      {data === null ? <Loader2 className="animate-spin text-tigris" /> : data.reviews.length === 0 ? (
        <p className="text-sm text-slate-400 bg-white rounded-2xl border border-slate-100 p-6 text-center" data-testid="reviews-empty">{r.empty}</p>
      ) : (
        <div className="grid sm:grid-cols-2 gap-4">
          {data.reviews.map((rv) => (
            <div key={rv.id} data-testid={`review-${rv.id}`} className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
              <div className="flex items-center justify-between mb-2">
                <span className="font-bold text-slate-800 text-sm">{rv.user_name}</span>
                <Stars value={rv.rating} size={14} />
              </div>
              <p className="text-sm text-slate-600 leading-relaxed">{rv.comment}</p>
              <p className="text-[11px] text-slate-400 mt-3">{new Date(rv.created_at).toLocaleDateString(lang === "ar" ? "ar-IQ" : "en-US")}</p>
            </div>
          ))}
        </div>
      )}
    </section>
  );
};
